/**
 * azure-functions/shared/state/statePatch.cjs
 *
 * Field-level patches for the tenant state blob (CONC-01, GH #7817).
 *
 * WHY THIS EXISTS
 *
 * A save used to send the browser's whole copy of the tenant blob under one
 * ROWVERSION. Every write anywhere in the tenant — another user in another
 * module, a writer endpoint the same tab just called, a server hook, a
 * sweeper — moved that version, so every open tab's next save was refused
 * (409 stale-version), and the tab's stale copy of keys it never touched was
 * part of what it tried to write. The client then had to reconstruct, after
 * the fact, which of those differences were its own edit. Concurrent use is
 * Tryggr's normal operating condition, so that was a product defect at the
 * write grain, not a race to manage.
 *
 * A save is now a list of operations at the grain of what the user changed:
 * "record R of collection K, field F: was V0, is V1". The server applies the
 * list to the CURRENT stored state, so:
 *   • edits to different keys, different records, or different fields of the
 *     same record never conflict — whoever saves second simply lands on top;
 *   • a change the tab never made is never sent, so it can never overwrite
 *     what someone else (or a server writer) stored;
 *   • only a genuine clash — the same field changed to different values on
 *     both sides — is refused, and the refusal names that field.
 *
 * The same module builds the patch in the browser (diffState) and applies it
 * on the server (applyPatch), so both sides agree on identity and equality by
 * construction rather than by convention.
 *
 * RECORD IDENTITY is a stable surrogate only (IDENTITY_KEYS). A positional
 * key (criticalityScale's `value`, renumbered on every save — #5065) is never
 * used: an array without a stable identity is compared as one value, which
 * can only ever over-report a clash, never merge an edit onto the wrong
 * record.
 */

'use strict';

/** Record identity keys, in preference order. Stable surrogates only. */
const IDENTITY_KEYS = ['id', 'obligationId'];

/**
 * Bookkeeping stamps every record edit writes. Two users editing different
 * fields of one record both stamp these, and that is not a clash between
 * them: the later save's stamp is the true one.
 */
const STAMP_FIELDS = new Set([
  'updatedAt',
  'updatedBy',
  'updatedByName',
  'lastUpdated',
  'lastModified',
  'lastModifiedAt',
  'lastModifiedBy',
  'modifiedAt',
  'modifiedBy',
]);

/**
 * Fields whose value is a compliance claim about the whole record — a status,
 * a verification, a completion, an approval, an outcome. Merging one of these
 * onto a record whose OTHER fields someone else changed meanwhile can assert a
 * state nobody asserted: B marks a task completed while A, at the same moment,
 * clears its evidence — each edit valid alone, the merge an over-claim
 * (TRYGGR_INTENT.md §5, §12; co-owner, CONC-01). So a change to one of these
 * clashes whenever any other field of its record moved since the user saw it,
 * and "keep mine" is never offered on one (src/state/patchConflicts.js).
 */
const STATUS_BEARING_FIELDS = new Set([
  'status',
  'state',
  'stage',
  'verified',
  'verifiedAt',
  'verifiedBy',
  'verificationStatus',
  'completed',
  'completedAt',
  'completedBy',
  'closedAt',
  'closedBy',
  'approved',
  'approvedAt',
  'approvedBy',
  'approval',
  'decision',
  'decidedAt',
  'decidedBy',
  'outcome',
  'result',
  'effectiveness',
  'effectivenessVerified',
  'enabled',
  'applicable',
]);

/** The status-bearing fields of a record, as one comparable value. */
function statusOf(record) {
  const out = {};
  if (isPlainObject(record)) {
    for (const f of Object.keys(record)) {
      if (STATUS_BEARING_FIELDS.has(f) && record[f] !== undefined) out[f] = record[f];
    }
  }
  return out;
}

/** Segments that could reach an object prototype. Never valid in a path. */
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);

const MAX_DEPTH = 12;
const MAX_OPS = 20000;

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function hasOwn(o, k) {
  return Object.prototype.hasOwnProperty.call(o, k);
}

/** JSON with object keys sorted, so key order never reads as a change. */
function stableStringify(v) {
  if (v === undefined) return undefined;
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) {
    return '[' + v.map((x) => (x === undefined ? 'null' : stableStringify(x))).join(',') + ']';
  }
  const keys = Object.keys(v)
    .filter((k) => v[k] !== undefined)
    .sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(v[k])).join(',') + '}';
}

function sameValue(a, b) {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return stableStringify(a) === stableStringify(b);
}

/** Two records equal apart from their bookkeeping stamps. */
function sameRecord(a, b) {
  if (!isPlainObject(a) || !isPlainObject(b)) return sameValue(a, b);
  const strip = (r) => {
    const out = {};
    for (const k of Object.keys(r)) if (!STAMP_FIELDS.has(k)) out[k] = r[k];
    return out;
  };
  return sameValue(strip(a), strip(b));
}

function clone(v) {
  return v === undefined ? undefined : JSON.parse(JSON.stringify(v));
}

function isIdValue(v) {
  return typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v));
}

/** The identity key every element of every given array carries uniquely, or null. */
function identityKeyFor(...arrays) {
  if (arrays.every((a) => a.length === 0)) return null;
  for (const k of IDENTITY_KEYS) {
    const ok = arrays.every((arr) => {
      const seen = new Set();
      for (const el of arr) {
        if (!isPlainObject(el) || !isIdValue(el[k])) return false;
        const key = typeof el[k] + ':' + el[k];
        if (seen.has(key)) return false;
        seen.add(key);
      }
      return true;
    });
    if (ok) return k;
  }
  return null;
}

function memberKey(x) {
  return typeof x + ':' + String(x);
}

/** Every element a string/number/boolean, none repeated. */
function isPrimitiveSet(arr) {
  const seen = new Set();
  for (const x of arr) {
    if (!['string', 'number', 'boolean'].includes(typeof x)) return false;
    const k = memberKey(x);
    if (seen.has(k)) return false;
    seen.add(k);
  }
  return true;
}

function sameOrder(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ── Diff ─────────────────────────────────────────────────────────────────────

function pushSet(ops, path, beforePresent, before, afterPresent, after) {
  const op = { op: 'set', path };
  if (beforePresent) op.before = clone(before);
  else op.beforeAbsent = true;
  if (afterPresent) op.after = clone(after);
  else op.afterAbsent = true;
  ops.push(op);
}

function diffValue(ops, path, before, after, depth) {
  if (sameValue(before, after)) return;
  if (depth < MAX_DEPTH && isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of keys) {
      const bp = hasOwn(before, k) && before[k] !== undefined;
      const ap = hasOwn(after, k) && after[k] !== undefined;
      if (!bp && !ap) continue;
      const from = ops.length;
      if (bp && ap) diffValue(ops, [...path, k], before[k], after[k], depth + 1);
      else pushSet(ops, [...path, k], bp, before[k], ap, after[k]);
      // Every change to a field of a record carries what the user saw of that
      // record, so the server can refuse a merge that asserts a state nobody
      // asserted — in BOTH orders (co-owner, CONC-01): a status change onto a
      // record whose other fields moved (recordBefore), and any other change
      // onto a record whose status moved (statusBefore).
      if (path.length > 0) {
        for (let i = from; i < ops.length; i++) {
          if (ops[i].op !== 'set' || ops[i].path.length !== path.length + 1) continue;
          if (STATUS_BEARING_FIELDS.has(k)) ops[i].recordBefore = clone(before);
          else ops[i].statusBefore = statusOf(before);
        }
      }
    }
    return;
  }
  if (depth < MAX_DEPTH && Array.isArray(before) && Array.isArray(after)) {
    const k = identityKeyFor(before, after);
    if (k) {
      diffRecords(ops, path, k, before, after, depth);
      return;
    }
    if (isPrimitiveSet(before) && isPrimitiveSet(after)) {
      const afterKeys = new Set(after.map(memberKey));
      const beforeKeys = new Set(before.map(memberKey));
      const kept = before.filter((x) => afterKeys.has(memberKey(x)));
      const keptInAfter = after.filter((x) => beforeKeys.has(memberKey(x)));
      // A pure membership change keeps the surviving members' order. A
      // reorder is a change of the whole value and goes as one.
      if (sameOrder(kept.map(memberKey), keptInAfter.map(memberKey))) {
        ops.push({
          op: 'members',
          path,
          add: after.filter((x) => !beforeKeys.has(memberKey(x))),
          remove: before.filter((x) => !afterKeys.has(memberKey(x))),
        });
        return;
      }
    }
  }
  pushSet(ops, path, true, before, true, after);
}

function diffRecords(ops, path, k, before, after, depth) {
  const id = (el) => el[k];
  const beforeById = new Map(before.map((el) => [memberKey(id(el)), el]));
  const afterById = new Map(after.map((el) => [memberKey(id(el)), el]));

  for (const el of before) {
    if (!afterById.has(memberKey(id(el)))) {
      ops.push({ op: 'remove', path, idKey: k, id: id(el), before: clone(el) });
    }
  }
  let prevId = null;
  after.forEach((el, i) => {
    const key = memberKey(id(el));
    const was = beforeById.get(key);
    if (!was) {
      // An append stays an append: two sessions each adding to the end of a
      // list both end up at the end, in save order.
      const op = { op: 'insert', path, idKey: k, record: clone(el), afterId: prevId };
      if (i === after.length - 1) op.atEnd = true;
      ops.push(op);
    } else {
      diffValue(ops, [...path, { k, v: id(el) }], was, el, depth + 1);
    }
    prevId = id(el);
  });
  const keptBefore = before
    .filter((el) => afterById.has(memberKey(id(el))))
    .map((el) => memberKey(id(el)));
  const keptAfter = after
    .filter((el) => beforeById.has(memberKey(id(el))))
    .map((el) => memberKey(id(el)));
  if (!sameOrder(keptBefore, keptAfter)) {
    ops.push({ op: 'order', path, idKey: k, ids: after.map(id) });
  }
}

/**
 * The operations that turn `base` into `next`, top-level key by key. Keys
 * equal on both sides produce nothing, so a save only ever carries what this
 * tab actually changed.
 */
function diffState(base, next) {
  const ops = [];
  const b = isPlainObject(base) ? base : {};
  const n = isPlainObject(next) ? next : {};
  const keys = new Set([...Object.keys(b), ...Object.keys(n)]);
  for (const k of keys) {
    const bp = hasOwn(b, k) && b[k] !== undefined;
    const np = hasOwn(n, k) && n[k] !== undefined;
    if (!bp && !np) continue;
    if (bp && np) diffValue(ops, [k], b[k], n[k], 1);
    else pushSet(ops, [k], bp, b[k], np, n[k]);
  }
  return ops;
}

// ── Validation ───────────────────────────────────────────────────────────────

function validSegment(seg, first) {
  if (typeof seg === 'string') return seg.length > 0 && !FORBIDDEN_SEGMENTS.has(seg);
  if (first) return false;
  return isPlainObject(seg) && IDENTITY_KEYS.includes(seg.k) && isIdValue(seg.v);
}

function validPath(path) {
  return Array.isArray(path) && path.length > 0 && path.every((s, i) => validSegment(s, i === 0));
}

/**
 * Throws a 400-shaped error when `ops` is not a well-formed patch. Run on the
 * server before anything is applied: a patch is client input.
 */
function validatePatch(ops) {
  const bad = (msg) => {
    const e = new Error(`Invalid patch: ${msg}`);
    e.code = 'INVALID_PATCH';
    throw e;
  };
  if (!Array.isArray(ops)) bad('not an array');
  if (ops.length > MAX_OPS) bad('too many operations');
  ops.forEach((op, i) => {
    if (!isPlainObject(op) || !validPath(op.path)) bad(`operation ${i} has no valid path`);
    switch (op.op) {
      case 'set':
        if (!op.beforeAbsent && !hasOwn(op, 'before')) bad(`operation ${i} has no before`);
        if (hasOwn(op, 'recordBefore') && !isPlainObject(op.recordBefore))
          bad(`operation ${i} has a bad recordBefore`);
        if (hasOwn(op, 'statusBefore') && !isPlainObject(op.statusBefore))
          bad(`operation ${i} has a bad statusBefore`);
        // A change inside a record must say what the user saw of it, or the
        // cross-field rule would rest on what the client chose to send.
        if (op.path.length >= 2) {
          const last = op.path[op.path.length - 1];
          if (STATUS_BEARING_FIELDS.has(last) && !isPlainObject(op.recordBefore))
            bad(`operation ${i} changes a status without the record it saw`);
          if (!STATUS_BEARING_FIELDS.has(last) && !isPlainObject(op.statusBefore))
            bad(`operation ${i} changes a record field without the status it saw`);
        }
        if (!op.afterAbsent && !hasOwn(op, 'after')) bad(`operation ${i} has no after`);
        break;
      case 'insert':
        if (
          !IDENTITY_KEYS.includes(op.idKey) ||
          !isPlainObject(op.record) ||
          !isIdValue(op.record[op.idKey])
        )
          bad(`operation ${i} inserts no identifiable record`);
        if (op.afterId != null && !isIdValue(op.afterId)) bad(`operation ${i} has a bad afterId`);
        break;
      case 'remove':
        if (!IDENTITY_KEYS.includes(op.idKey) || !isIdValue(op.id) || !hasOwn(op, 'before'))
          bad(`operation ${i} removes no identifiable record`);
        break;
      case 'order':
        if (!IDENTITY_KEYS.includes(op.idKey) || !Array.isArray(op.ids) || !op.ids.every(isIdValue))
          bad(`operation ${i} has a bad order`);
        break;
      case 'members':
        if (!Array.isArray(op.add) || !Array.isArray(op.remove))
          bad(`operation ${i} has bad members`);
        break;
      default:
        bad(`operation ${i} has unknown kind`);
    }
  });
}

// ── Apply ────────────────────────────────────────────────────────────────────

const GONE = Symbol('gone');

function step(container, seg) {
  if (typeof seg === 'string') {
    if (!isPlainObject(container) || !hasOwn(container, seg)) return GONE;
    return container[seg];
  }
  if (!Array.isArray(container)) return GONE;
  const found = container.find((el) => isPlainObject(el) && el[seg.k] === seg.v);
  return found === undefined ? GONE : found;
}

function navigate(root, path) {
  let cur = root;
  for (const seg of path) {
    cur = step(cur, seg);
    if (cur === GONE) return GONE;
  }
  return cur;
}

function topKeyOf(op) {
  return op.path[0];
}

/**
 * Apply `ops` to a copy of `base`.
 *
 * Returns `{ state, conflicts, changedKeys }`. Every operation that does not
 * clash is applied; each one that does is left unapplied and reported, with
 * the value it expected (`before`), the value it carried (`mine`) and the
 * value it found (`theirs`). The server writes nothing when `conflicts` is
 * non-empty; the browser uses the same result to rebase its edit.
 *
 * An operation whose result is already in place (the other writer made the
 * same change — typically this tab's own writer endpoint) is a no-op, not a
 * clash.
 */
function applyPatch(base, ops) {
  const state = isPlainObject(base) ? clone(base) : {};
  const original = isPlainObject(base) ? base : {};
  const conflicts = [];
  const changedKeys = new Set();
  // For a change that lands under a record someone else deleted: which
  // record, so the user is told what went and can restore it with their edit
  // or let the delete stand (CONC-01, GUARD-01 condition 3).
  const goneRecord = (path) => {
    for (let i = 0; i < path.length; i++) {
      const seg = path[i];
      if (typeof seg === 'string') continue;
      if (navigate(state, path.slice(0, i + 1)) === GONE) {
        return { collection: path.slice(0, i), idKey: seg.k, id: seg.v };
      }
    }
    return null;
  };
  const clash = (op, kind, theirs, mine) =>
    conflicts.push({
      path: op.path,
      kind,
      ...(kind === 'gone' && goneRecord(op.path) ? { record: goneRecord(op.path) } : {}),
      ...(op.op !== 'set'
        ? { op: op.op, idKey: op.idKey, id: op.op === 'insert' ? op.record[op.idKey] : op.id }
        : {}),
      ...(op.before !== undefined ? { before: op.before } : {}),
      ...(mine !== undefined ? { mine } : {}),
      ...(theirs !== undefined && theirs !== GONE ? { theirs } : {}),
    });

  for (const op of ops) {
    if (op.op === 'set') {
      const parent = navigate(state, op.path.slice(0, -1));
      const last = op.path[op.path.length - 1];
      if (parent === GONE || !isPlainObject(parent)) {
        if (op.afterAbsent) continue; // deleting under something already gone
        clash(op, 'gone', undefined, op.after);
        continue;
      }
      const present = hasOwn(parent, last) && parent[last] !== undefined;
      const cur = parent[last];
      const matchesAfter = op.afterAbsent ? !present : present && sameValue(cur, op.after);
      if (matchesAfter) continue;
      const matchesBefore = op.beforeAbsent ? !present : present && sameValue(cur, op.before);
      if (!matchesBefore && !STAMP_FIELDS.has(last)) {
        clash(op, 'changed', present ? clone(cur) : undefined, op.after);
        continue;
      }
      if (op.recordBefore !== undefined) {
        // Judged against the record as stored BEFORE this patch: the user's
        // own sibling edits in the same save are part of what they asserted.
        const stored = navigate(original, op.path.slice(0, -1));
        const others = (r) => {
          const out = {};
          if (isPlainObject(r))
            for (const f of Object.keys(r)) if (f !== last && !STAMP_FIELDS.has(f)) out[f] = r[f];
          return out;
        };
        if (stored === GONE || !sameValue(others(stored), others(op.recordBefore))) {
          clash(op, 'record-changed', stored === GONE ? undefined : clone(stored), op.after);
          continue;
        }
      }
      if (op.statusBefore !== undefined) {
        const stored = navigate(original, op.path.slice(0, -1));
        if (stored !== GONE && !sameValue(statusOf(stored), op.statusBefore)) {
          clash(op, 'record-changed', clone(stored), op.after);
          continue;
        }
      }
      if (op.afterAbsent) delete parent[last];
      else parent[last] = clone(op.after);
      changedKeys.add(topKeyOf(op));
      continue;
    }

    const target = navigate(state, op.path);
    if (op.op === 'members') {
      let arr = target;
      if (arr === GONE || arr === undefined) {
        const parent = navigate(state, op.path.slice(0, -1));
        if (parent === GONE || !isPlainObject(parent)) {
          clash(op, 'gone', undefined, op.add);
          continue;
        }
        arr = [];
        parent[op.path[op.path.length - 1]] = arr;
      }
      if (!Array.isArray(arr)) {
        clash(op, 'shape', clone(arr), op.add);
        continue;
      }
      const removeKeys = new Set(op.remove.map(memberKey));
      const next = arr.filter((x) => !removeKeys.has(memberKey(x)));
      const have = new Set(next.map(memberKey));
      for (const x of op.add) if (!have.has(memberKey(x))) next.push(x);
      if (!sameValue(next, arr)) {
        arr.splice(0, arr.length, ...next);
        changedKeys.add(topKeyOf(op));
      }
      continue;
    }

    if (target === GONE || !Array.isArray(target)) {
      if (op.op === 'remove' || op.op === 'order') continue; // nothing left to act on
      if (target === GONE || target === undefined) {
        const parent = navigate(state, op.path.slice(0, -1));
        if (parent !== GONE && isPlainObject(parent)) {
          parent[op.path[op.path.length - 1]] = [clone(op.record)];
          changedKeys.add(topKeyOf(op));
          continue;
        }
      }
      clash(
        op,
        target === GONE ? 'gone' : 'shape',
        target === GONE ? undefined : clone(target),
        op.record
      );
      continue;
    }

    const idx = (id) => target.findIndex((el) => isPlainObject(el) && el[op.idKey] === id);
    if (op.op === 'insert') {
      const at = idx(op.record[op.idKey]);
      if (at !== -1) {
        if (!sameRecord(target[at], op.record)) clash(op, 'exists', clone(target[at]), op.record);
        continue;
      }
      let pos = target.length;
      if (op.atEnd) pos = target.length;
      else if (op.afterId == null) pos = 0;
      else {
        const prev = idx(op.afterId);
        if (prev !== -1) pos = prev + 1;
      }
      target.splice(pos, 0, clone(op.record));
      changedKeys.add(topKeyOf(op));
    } else if (op.op === 'remove') {
      const at = idx(op.id);
      if (at === -1) continue;
      if (!sameRecord(target[at], op.before)) {
        clash(op, 'changed', clone(target[at]), undefined);
        continue;
      }
      target.splice(at, 1);
      changedKeys.add(topKeyOf(op));
    } else if (op.op === 'order') {
      const rank = new Map(op.ids.map((id, i) => [memberKey(id), i]));
      const slots = [];
      const members = [];
      target.forEach((el, i) => {
        if (isPlainObject(el) && rank.has(memberKey(el[op.idKey]))) {
          slots.push(i);
          members.push(el);
        }
      });
      members.sort((a, b) => rank.get(memberKey(a[op.idKey])) - rank.get(memberKey(b[op.idKey])));
      let moved = false;
      slots.forEach((slot, i) => {
        if (target[slot] !== members[i]) moved = true;
        target[slot] = members[i];
      });
      if (moved) changedKeys.add(topKeyOf(op));
    }
  }

  return { state, conflicts, changedKeys: [...changedKeys] };
}

/** The value at `path` in `state`, and whether anything is there. */
function valueAt(state, path) {
  const v = navigate(state, path);
  return v === GONE ? { present: false } : { present: v !== undefined, value: v };
}

/**
 * What a patch did, for the audit trail: one entry per operation naming its
 * kind and where it acted — a record created, a record deleted, a field set,
 * an item added or removed — never the values (LOG-B-001 records acts, not
 * content). Both users' acts reach the log, deletes included.
 */
function auditSummary(ops, limit = 500) {
  const list = (ops || []).slice(0, limit).map((op) => {
    const entry = {
      op: op.op === 'set' ? (op.afterAbsent ? 'unset' : 'set') : op.op,
      path: op.path,
    };
    if (op.op === 'insert') entry.id = op.record && op.record[op.idKey];
    if (op.op === 'remove') entry.id = op.id;
    if (op.op === 'members') {
      entry.added = op.add.length;
      entry.removed = op.remove.length;
    }
    if (op.override) entry.override = true;
    return entry;
  });
  return { list, truncated: Math.max(0, (ops || []).length - limit) };
}

/** Top-level keys a patch touches. */
function patchKeys(ops) {
  return [
    ...new Set((ops || []).map((op) => op && Array.isArray(op.path) && op.path[0]).filter(Boolean)),
  ];
}

module.exports = {
  IDENTITY_KEYS,
  STAMP_FIELDS,
  STATUS_BEARING_FIELDS,
  statusOf,
  diffState,
  applyPatch,
  validatePatch,
  patchKeys,
  auditSummary,
  valueAt,
  sameValue,
  stableStringify,
};
